import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import api from "@/lib/api";
import { toast } from "sonner";
import { Gift, X, Bell, PartyPopper } from "lucide-react";
import { isRefCardVisible, subscribeRefCard } from "@/lib/refCard";

const WEEK = 7 * 86400000;

// Slim announcement strip — inline flow, only on Dashboard & Profile, dismiss hides for 7 days.
export function ReferralStrip() {
  const loc = useLocation();
  const [announce, setAnnounce] = useState("");
  const [show, setShow] = useState(false);
  const [cardVisible, setCardVisible] = useState(isRefCardVisible());
  const allowed = loc.pathname === "/app" || loc.pathname === "/app/profile";

  useEffect(() => subscribeRefCard(setCardVisible), []);

  useEffect(() => {
    api.get("/referral/config").then(({ data }) => {
      if (!data.enabled) return;
      setAnnounce(data.announcement_text || "");
      const until = parseInt(localStorage.getItem("pm_announce_dismiss") || "0", 10);
      if (Date.now() > until) setShow(true);
    }).catch(() => {});
  }, []);

  const dismiss = () => { setShow(false); localStorage.setItem("pm_announce_dismiss", String(Date.now() + WEEK)); };
  // Never show the slim strip while the richer login card is visible.
  if (!allowed || !show || !announce || cardVisible) return null;

  return (
    <div className="flex items-center gap-2 h-9 max-h-9 overflow-hidden bg-thali-turmeric/90 text-thali-ink rounded-lg px-3 mb-4 shimmer" data-testid="referral-strip">
      <Link to="/app/refer" className="flex-1 min-w-0 truncate text-[13px] font-semibold leading-none hover:underline">{announce}</Link>
      <button onClick={dismiss} className="shrink-0 p-1 -mr-1" aria-label="Dismiss" data-testid="referral-strip-dismiss"><X className="w-4 h-4" /></button>
    </div>
  );
}

export default function ReferralExtras({ admin = false }) {
  const { t } = useLang();
  const nav = useNavigate();
  const loc = useLocation();
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [openBell, setOpenBell] = useState(false);
  const [celebrate, setCelebrate] = useState(null);
  const [pulse, setPulse] = useState(true);
  const seen = useRef(false);

  // Pulse the gift button only once on load.
  useEffect(() => { const id = setTimeout(() => setPulse(false), 2600); return () => clearTimeout(id); }, []);

  useEffect(() => {
    let stop = false;
    const poll = async () => {
      try {
        const { data } = await api.get("/notifications");
        if (stop) return;
        setNotifs(data.notifications);
        setUnread(data.unread);
        if (seen.current) {
          const newUnread = data.notifications.filter((n) => !n.read);
          const reward = newUnread.find((n) => n.celebrate);
          if (reward) { setCelebrate(reward); api.post("/notifications/read", { ids: [reward.id] }).catch(() => {}); }
          else if (newUnread[0]) toast.success(newUnread[0].text);
        }
        seen.current = true;
      } catch {}
    };
    poll();
    const id = setInterval(poll, 20000);
    return () => { stop = true; clearInterval(id); };
  }, []);

  // Auto-hide the celebration banner after 4 seconds.
  useEffect(() => {
    if (!celebrate) return;
    const id = setTimeout(() => setCelebrate(null), 4000);
    return () => clearTimeout(id);
  }, [celebrate]);

  const markRead = async () => { try { await api.post("/notifications/read", {}); setUnread(0); setNotifs((n) => n.map((x) => ({ ...x, read: true }))); } catch {} };

  // Hide the gift button on the entry/report screens where export bars & steppers live.
  const hideFab = admin || /^\/app\/(entry|reports|bulk|history|refer|builder)/.test(loc.pathname);

  return (
    <>
      {/* Notification bell */}
      <button onClick={() => { setOpenBell((o) => !o); if (!openBell) markRead(); }} data-testid="notif-bell"
        className="fixed top-3 right-4 z-40 w-10 h-10 rounded-full bg-white shadow-lg grid place-items-center border border-thali-turmeric/30">
        <Bell className="w-5 h-5 text-thali-green" />
        {unread > 0 && <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 grid place-items-center" data-testid="notif-count">{unread}</span>}
      </button>
      {openBell && (
        <div className="fixed top-14 right-4 z-40 w-72 max-h-80 overflow-y-auto bg-white rounded-xl shadow-2xl border border-thali-turmeric/30 p-2" data-testid="notif-panel">
          {notifs.length === 0 ? <div className="text-sm text-thali-ink/50 p-3 text-center">No notifications</div> :
            notifs.map((n) => (<div key={n.id} className="text-sm p-2 rounded-lg hover:bg-thali-cream">{n.text}</div>))}
        </div>
      )}

      {/* Small floating gift button (44x44), stacked above the Suggestion button */}
      {!hideFab && (
        <button onClick={() => nav("/app/refer")} data-testid="gift-fab" aria-label={t("refer_earn")}
          className={`fixed right-4 bottom-[140px] md:bottom-[88px] z-40 w-11 h-11 rounded-full bg-thali-saffron text-white shadow-btn-saffron grid place-items-center ${pulse ? "pulse-once" : ""}`}>
          <Gift className="w-5 h-5" />
        </button>
      )}

      {/* Non-blocking reward banner (auto-hides 4s) with a short confetti burst */}
      {celebrate && (
        <div className="fixed top-3 inset-x-0 z-[60] flex justify-center px-4 pointer-events-none" data-testid="reward-banner">
          <div className="relative overflow-hidden pointer-events-auto max-w-sm w-full bg-thali-green text-white rounded-2xl shadow-2xl px-4 py-3 animate-fade-in-up">
            <div className="confetti">{Array.from({ length: 14 }).map((_, i) => <span key={i} style={{ left: `${(i * 7) % 100}%`, animationDelay: `${(i % 6) * 0.12}s`, background: ["#FBBF24", "#FFFFFF", "#F97316", "#DB2777"][i % 4] }} />)}</div>
            <div className="relative flex items-center gap-2">
              <PartyPopper className="w-5 h-5 shrink-0" />
              <div className="text-sm font-bold leading-tight">{celebrate.text}</div>
              <button onClick={() => setCelebrate(null)} className="ml-auto shrink-0 opacity-80" aria-label="Close" data-testid="reward-banner-close"><X className="w-4 h-4" /></button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
